import React from 'react'
import {
  Grid,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Chip,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import ImageCarosel from '../../Component/Carosel/Image_Carosel'

const ModernGallery = ({ getdata = [] }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (getdata.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          borderRadius: '16px',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No content available
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        maxWidth: 1440,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 6,
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Featured Item (First Item) */}
      <Box
        sx={{
          mb: 6,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: theme.shadows[4],
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <ImageCarosel
            image_data={getdata[0].image_urls}
            imageStyle={{
              width: '100%',
              height: isMobile ? '350px' : '500px',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 4,
              background: `linear-gradient(to top, ${alpha(
                theme.palette.common.black,
                0.7
              )} 0%, transparent 100%)`,
            }}
          >
            <Chip
              label={getdata[0]?.date || 'Latest'}
              sx={{
                mb: 2,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                fontWeight: 'bold',
              }}
            />
            <Typography
              variant="h3"
              sx={{
                color: theme.palette.common.white,
                fontWeight: 700,
                mb: 1,
              }}
            >
              {getdata[0]?.heading}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {getdata[0]?.description}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Gallery Grid */}
      <Grid item xs={12} md={4}>
        {getdata.slice(1).map((item, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: '16px',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              transition: 'all 0.3s ease',
              display: 'flex', // Changed to flex layout
              gap: 2, // Added gap between image and content
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[2],
              },
            }}
          >
            <Box
              sx={{
                width: '120px', // Fixed width for image
                minWidth: '120px', // Prevent shrinking
                height: '120px', // Fixed height
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <ImageCarosel
                image_data={item.image_urls}
                imageStyle={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1, // Take remaining space
                overflow: 'hidden',
              }}
            >
              <Typography
                variant="subtitle1" // Changed to subtitle1 for better hierarchy
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.heading || 'No Title'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  flex: 1, // Take available vertical space
                  color: theme.palette.text.secondary,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                }}
              >
                {item.description || 'No Description Available.'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 1,
                  pt: 1,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  {item.date || 'No Date'}
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    p: 0.5,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                ></IconButton>
              </Box>
            </Box>
          </Box>
        ))}
      </Grid>
    </Box>
  )
}

export default ModernGallery
